// src/services/api.js - API service for backend communication
import logger from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Get token from localStorage
const getToken = () => localStorage.getItem('authToken');

// Set common headers
const getHeaders = (includeAuth = true) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (includeAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  return headers;
};

// Handle API responses with logging
const handleResponse = async (response) => {
  try {
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || `API Error: ${response.status}`;
      logger.warn('API request failed', {
        status: response.status,
        message: errorMessage,
        endpoint: response.url,
      });
      throw new Error(errorMessage);
    }

    logger.debug('API request successful', {
      status: response.status,
      endpoint: response.url,
    });
    return data;
  } catch (error) {
    if (error instanceof SyntaxError) {
      logger.error('Invalid JSON response from server', {
        endpoint: response.url,
        error: error.message,
      });
      throw new Error('Invalid response from server');
    }
    throw error;
  }
};

// AUTHENTICATION ENDPOINTS
export const authAPI = {
  register: async (userData) => {
    try {
      logger.info('Attempting user registration', { email: userData.email });
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify(userData),
      });
      const data = await handleResponse(response);
      logger.info('User registration successful', { email: userData.email });
      return data;
    } catch (error) {
      logger.error('User registration failed', { email: userData.email, error: error.message });
      throw error;
    }
  },

  login: async (email, password) => {
    try {
      logger.info('Attempting user login', { email });
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify({ email, password }),
      });
      const data = await handleResponse(response);
      logger.info('User login successful', { email });
      return data;
    } catch (error) {
      logger.error('User login failed', { email, error: error.message });
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        method: 'GET',
        headers: getHeaders(true),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Failed to fetch current user', { error: error.message });
      throw error;
    }
  },

  logout: () => {
    logger.info('User logging out');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },
  
  forgotPassword: async (email) => {
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify({ email }),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Forgot password failed', { email, error: error.message });
      throw error;
    }
  },

  resetPassword: async (token, password, confirmPassword) => {
    try {
      const response = await fetch(`${API_URL}/auth/reset-password/${token}`, {
        method: 'PUT',
        headers: getHeaders(false),
        body: JSON.stringify({ password, confirmPassword }),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Reset password failed', { error: error.message });
      throw error;
    }
  },

  googleLogin: async (token) => {
    try {
      logger.info('Attempting Google login');
      // token can be either { idToken } or { accessToken }
      const response = await fetch(`${API_URL}/auth/google`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify(token),
      });
      const data = await handleResponse(response);
      logger.info('Google login successful');
      return data;
    } catch (error) {
      logger.error('Google login failed', { error: error.message });
      throw error;
    }
  },

  verifyEmail: async (verificationCode) => {
    try {
      logger.info('Verifying email address');
      const response = await fetch(`${API_URL}/auth/verify-email`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ verificationCode }),
      });
      const data = await handleResponse(response);
      logger.info('Email verified successfully');
      return data;
    } catch (error) {
      logger.error('Email verification failed', { error: error.message });
      throw error;
    }
  },

  resendVerification: async () => {
    try {
      logger.info('Resending verification email');
      const response = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({}),
      });
      const data = await handleResponse(response);
      logger.info('Verification email resent');
      return data;
    } catch (error) {
      logger.error('Failed to resend verification email', { error: error.message });
      throw error;
    }
  },
};

// USER ENDPOINTS
export const userAPI = {
  getProfile: async () => {
    const response = await fetch(`${API_URL}/users/profile`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  updateProfile: async (userData) => {
    const response = await fetch(`${API_URL}/users/profile`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  getUserById: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      method: 'GET',
      headers: getHeaders(false),
    });
    return handleResponse(response);
  },

  getAllUsers: async (filters = {}) => {
    const query = new URLSearchParams();
    if (filters.search) query.append('search', filters.search);
    if (filters.page) query.append('page', filters.page);
    if (filters.limit) query.append('limit', filters.limit);

    const response = await fetch(`${API_URL}/users${query.toString() ? '?' + query.toString() : ''}`, {
      method: 'GET',
      headers: getHeaders(false),
    });
    return handleResponse(response);
  },

  followUser: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}/follow`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  unfollowUser: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}/unfollow`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },
};

// MATCH ENDPOINTS
export const matchAPI = {
  getAllMatches: async (filters = {}) => {
    const query = new URLSearchParams();
    if (filters.sport) query.append('sport', filters.sport);
    if (filters.skillLevel) query.append('skillLevel', filters.skillLevel);
    if (filters.location) query.append('location', filters.location);
    if (filters.status) query.append('status', filters.status);
    if (filters.search) query.append('search', filters.search);

    const response = await fetch(
      `${API_URL}/matches${query.toString() ? '?' + query.toString() : ''}`,
      {
        method: 'GET',
        headers: getHeaders(false),
      }
    );
    return handleResponse(response);
  },

  getMatchById: async (matchId) => {
    const response = await fetch(`${API_URL}/matches/${matchId}`, {
      method: 'GET',
      headers: getHeaders(false),
    });
    return handleResponse(response);
  },

  createMatch: async (matchData) => {
    const response = await fetch(`${API_URL}/matches`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(matchData),
    });
    return handleResponse(response);
  },

  updateMatch: async (matchId, matchData) => {
    const response = await fetch(`${API_URL}/matches/${matchId}`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(matchData),
    });
    return handleResponse(response);
  },

  deleteMatch: async (matchId) => {
    const response = await fetch(`${API_URL}/matches/${matchId}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  joinMatch: async (matchId) => {
    try {
      logger.info('Attempting to join match', { matchId });
      const response = await fetch(`${API_URL}/matches/${matchId}/join`, {
        method: 'POST',
        headers: getHeaders(true),
      });
      const data = await handleResponse(response);
      logger.info('Successfully joined match', { matchId });
      return data;
    } catch (error) {
      logger.error('Failed to join match', { matchId, error: error.message });
      throw error;
    }
  },

  requestJoinMatch: async (matchId, message, answers) => {
    try {
      logger.info('Attempting to request to join match', { matchId });
      const response = await fetch(`${API_URL}/matches/${matchId}/request-join`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ message, answers }),
      });
      return await handleResponse(response);
    } catch (error) {
      logger.error('Failed to request to join match', { matchId, error: error.message });
      throw error;
    }
  },

  approveJoinRequest: async (matchId, userId) => {
    try {
      logger.info('Approving join request', { matchId, userId });
      const response = await fetch(`${API_URL}/matches/${matchId}/approve-request/${userId}`, {
        method: 'POST',
        headers: getHeaders(true),
      });
      return await handleResponse(response);
    } catch (error) {
      logger.error('Failed to approve join request', { matchId, userId, error: error.message });
      throw error;
    }
  },

  rejectJoinRequest: async (matchId, userId) => {
    try {
      logger.info('Rejecting join request', { matchId, userId });
      const response = await fetch(`${API_URL}/matches/${matchId}/reject-request/${userId}`, {
        method: 'POST',
        headers: getHeaders(true),
      });
      return await handleResponse(response);
    } catch (error) {
      logger.error('Failed to reject join request', { matchId, userId, error: error.message });
      throw error;
    }
  },

  leaveMatch: async (matchId) => {
    try {
      logger.info('Attempting to leave match', { matchId });
      const response = await fetch(`${API_URL}/matches/${matchId}/leave`, {
        method: 'POST',
        headers: getHeaders(true),
      });
      const data = await handleResponse(response);
      logger.info('Successfully left match', { matchId });
      return data;
    } catch (error) {
      logger.error('Failed to leave match', { matchId, error: error.message });
      throw error;
    }
  },

  getUserMatches: async (userId) => {
    const response = await fetch(`${API_URL}/matches/user/${userId}`, {
      method: 'GET',
      headers: getHeaders(false),
    });
    return handleResponse(response);
  },
};

// MESSAGE ENDPOINTS
export const messageAPI = {
  getMessages: async (matchId) => {
    try {
      const response = await fetch(`${API_URL}/messages/${matchId}`, {
        method: 'GET',
        headers: getHeaders(true),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Failed to fetch messages', { matchId, error: error.message });
      throw error;
    }
  },

  sendMessage: async (matchId, message) => {
    try {
      const response = await fetch(`${API_URL}/messages/${matchId}`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ message }),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Failed to send message', { matchId, error: error.message });
      throw error;
    }
  },

  deleteMessage: async (messageId) => {
    try {
      const response = await fetch(`${API_URL}/messages/${messageId}`, {
        method: 'DELETE',
        headers: getHeaders(true),
      });
      return handleResponse(response);
    } catch (error) {
      logger.error('Failed to delete message', { messageId, error: error.message });
      throw error;
    }
  },
};

// NOTIFICATION ENDPOINTS
export const notificationAPI = {
  getNotifications: async () => {
    const response = await fetch(`${API_URL}/notifications`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  markAsRead: async (id) => {
    const response = await fetch(`${API_URL}/notifications/${id}/read`, {
      method: 'PUT',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  markAllAsRead: async () => {
    const response = await fetch(`${API_URL}/notifications/read-all`, {
      method: 'PUT',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  toggleMuteMatch: async (matchId) => {
    const response = await fetch(`${API_URL}/notifications/mute/${matchId}`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },
};

export default {
  authAPI,
  userAPI,
  matchAPI,
  messageAPI,
  notificationAPI,
};
